import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Globe } from "lucide-react";
import * as Icons from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { fetchProducts } from "@/lib/api";
import { RETAILERS } from "@/lib/retailers";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const CATEGORY_ORDER = ["Editions", "Bundles", "Signed Editions", "Tools", "Merch", "Printables"];
const CATEGORY_BLURB = {
  Editions: "Founder's launch pricing on every format.",
  Bundles: "Curated collections designed for the movement — save more, do more together.",
  "Signed Editions": "Hand-signed and personalized — official store only.",
  Tools: "Take the framework deeper — companion workbook, toolkit, and facilitator license.",
  Merch: "Live the framework daily — practical goods in the Love Genome palette.",
  Printables: "Instant-download PDFs — bookmarks, cards, wallpapers, and the 10-year companion calendar.",
};

const MerchTile = ({ product }) => {
  const Icon = Icons[product.icon] || Icons.Sparkles;
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-emerald">
      <span className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center text-gold">
        <Icon className="w-7 h-7" strokeWidth={1.4} />
      </span>
      <Logo className="w-8 h-8" />
    </div>
  );
};

const ProductCard = ({ p, addItem }) => (
  <article className="group bg-cream p-8 md:p-10 h-full flex flex-col" data-testid={`product-card-${p.id}`}>
    <Link to={`/product/${p.id}`} className="block relative overflow-hidden bg-surface aspect-[3/4] mb-8 border border-line">
      {p.merch ? (
        <MerchTile product={p} />
      ) : (
        <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      )}
      {p.badge && (
        <span className="absolute top-4 left-4 bg-emerald text-cream text-[10px] uppercase tracking-widest px-3 py-1.5 z-10">{p.badge}</span>
      )}
      <span className="absolute bottom-4 right-4 w-10 h-10 group-hover:bg-gold flex items-center justify-center transition-all duration-500 opacity-0 group-hover:opacity-100 z-10">
        <ArrowUpRight className="w-5 h-5 text-ink" />
      </span>
    </Link>
    <div className="flex-1 flex flex-col">
      <p className="eyebrow text-ink/40 mb-3">{p.format}</p>
      <Link to={`/product/${p.id}`}>
        <h3 className="font-display text-2xl text-ink leading-tight mb-3 group-hover:text-emerald transition-colors">
          {p.name.replace("The Love Genome — ", "").replace("Love Genome ", "")}
        </h3>
      </Link>
      <p className="font-body text-ink/60 text-sm leading-relaxed mb-6">{p.tagline}</p>
      <div className="mt-auto flex items-center justify-between border-t border-line pt-6">
        <span className="font-display text-2xl text-emerald" data-testid={`product-price-${p.id}`}>${p.price.toFixed(2)}</span>
        <button
          onClick={() => { addItem(p); toast.success(`Added to cart`); }}
          className="btn-emerald !px-5 !py-2.5 text-sm"
          data-testid={`add-to-cart-${p.id}`}
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  </article>
);

const Store = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => toast.error("Could not load the store.")).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of products) {
      const c = p.category || "Editions";
      (g[c] = g[c] || []).push(p);
    }
    return g;
  }, [products]);

  return (
    <main className="bg-cream min-h-screen pt-32" data-testid="store-page">
      <section className="bg-emerald text-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-6">The Store</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
              Books, tools & <span className="italic text-gold">everyday goods</span>.
            </h1>
            <p className="font-editorial italic text-2xl text-cream/80 mt-8 max-w-xl">
              From founder's-launch editions to practical merchandise for daily, weekly, and yearly practice.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-cream aspect-[3/4] animate-pulse" />)}
            </div>
          ) : (
            CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
              <div key={cat} className="mb-20" data-testid={`category-${cat.toLowerCase().replace(/\s/g, "-")}`}>
                <Reveal>
                  <div className="flex flex-wrap items-end justify-between gap-4 mb-8 border-b border-line pb-5">
                    <h2 className="font-display text-3xl md:text-5xl text-ink">{cat}</h2>
                    <p className="font-body text-ink/50 text-sm max-w-sm md:text-right">{CATEGORY_BLURB[cat]}</p>
                  </div>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
                  {grouped[cat].map((p) => (
                    <Reveal key={p.id}><ProductCard p={p} addItem={addItem} /></Reveal>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Available worldwide */}
      <section className="bg-surface py-20 md:py-28 border-t border-line" data-testid="retailers">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-emerald mb-6 flex items-center gap-3"><Globe className="w-4 h-4" /> Available Worldwide</p>
            <h2 className="font-display text-3xl md:text-5xl text-ink mb-4">Also available at your favorite retailer.</h2>
            <p className="font-body text-ink/60 max-w-2xl mb-12">
              Prefer to shop elsewhere? The Love Genome™ is being published across major global retailers. Signed and collector's editions are exclusive to this official store.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-4" data-testid="retailer-links">
            {RETAILERS.map((r) => (
              <a key={r.key} href={r.url} target="_blank" rel="noopener noreferrer"
                className="btn-outline !py-3 !px-6 text-sm" data-testid={`retailer-${r.key}`}>
                {r.label} <ArrowUpRight className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Store;
